import { useQuery } from "@tanstack/react-query";
import { type FC, useId, useMemo, useState } from "react";

import { m } from "@recobra/i18n/messages";
import { type DocumentType, REQUESTABLE_DOCUMENT_TYPES } from "@recobra/schemas/document";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@recobra/ui/alert-dialog";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@recobra/ui/combobox";
import { Field, FieldDescription, FieldError, FieldLabel } from "@recobra/ui/field";
import { ItemGroup } from "@recobra/ui/item";

import { displayDocFromRecord } from "../../documents";
import { getDocumentType } from "../../documents";
import { sortByLabel } from "../../lib";
import { documentsApi } from "../../lib/api";
import { RequestedDocumentRow } from "./RequestedDocumentRow";
import type { TaskFormValues } from "./TaskFormValues";
import type { TaskCreationFormApi } from "./useTaskCreationForm";

/** The documents this task asks for; a type may be asked for more than once. */
export const RequestedDocumentsSection: FC<{
  form: TaskCreationFormApi;
  contextType: "case" | "client";
  contextId: string;
  hideLabel?: boolean;
}> = ({ form, contextType, contextId, hideLabel }) => {
  const comboboxId = useId();
  const caseId = contextType === "case" ? contextId : undefined;

  const { data: caseDocuments } = useQuery({
    queryKey: ["cases", caseId, "documents"],
    queryFn: () => documentsApi.listDocuments(caseId!),
    enabled: !!caseId,
  });

  const existingDocumentTypes = useMemo(() => {
    const types = new Set<DocumentType>();
    for (const doc of caseDocuments?.items ?? []) {
      if (doc.deletedAt) continue;
      const documentType = getDocumentType(displayDocFromRecord(doc));
      if (documentType) types.add(documentType);
    }

    return types;
  }, [caseDocuments]);

  const [pendingDocumentType, setPendingDocumentType] = useState<DocumentType | null>(null);
  const documentTypeOptions = sortByLabel(
    REQUESTABLE_DOCUMENT_TYPES,
    (documentType) => m.document_type_label({ documentType }),
    "other",
  );

  return (
    <form.Field
      name="requestedDocuments"
      mode="array"
      validators={{ onChange: ({ value }) => describeUnlabelledOther(value) }}
    >
      {(field) => {
        const requested = field.state.value;
        const error = field.state.meta.errors[0];
        const addRequestedDocument = (documentType: DocumentType) =>
          // rows are told apart only by identity, so give each one a key; the API issues the stored one
          field.pushValue({ documentType, key: `draft_${crypto.randomUUID()}` });

        return (
          <>
            <Field data-invalid={error ? true : undefined}>
              {hideLabel ? null : (
                <FieldLabel htmlFor={comboboxId}>{m.task_requested_documents_label()}</FieldLabel>
              )}

              <Combobox
                items={documentTypeOptions}
                value={null}
                itemToStringLabel={(documentType: DocumentType) =>
                  m.document_type_label({ documentType })
                }
                onValueChange={(documentType: DocumentType | null) => {
                  if (!documentType) return;
                  if (existingDocumentTypes.has(documentType)) {
                    setPendingDocumentType(documentType);
                    return;
                  }
                  addRequestedDocument(documentType);
                }}
              >
                <ComboboxInput
                  id={comboboxId}
                  className="w-full"
                  aria-label={hideLabel ? m.task_requested_documents_label() : undefined}
                  placeholder={m.task_requested_documents_add()}
                />
                <ComboboxContent>
                  <ComboboxEmpty>{m.task_requested_documents_no_match()}</ComboboxEmpty>
                  <ComboboxList>
                    {(documentType: DocumentType) => (
                      <ComboboxItem key={documentType} value={documentType}>
                        {m.document_type_label({ documentType })}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>

              {requested.length === 0 ? (
                <FieldDescription>{m.task_requested_documents_empty()}</FieldDescription>
              ) : (
                <ItemGroup>
                  {requested.map((entry, index) => (
                    <RequestedDocumentRow
                      key={entry.key}
                      entry={entry}
                      onLabelChange={(label) => field.replaceValue(index, { ...entry, label })}
                      onRemove={() => field.removeValue(index)}
                    />
                  ))}
                </ItemGroup>
              )}

              {error ? <FieldError>{error}</FieldError> : null}
            </Field>

            <AlertDialog
              open={pendingDocumentType !== null}
              onOpenChange={(open) => !open && setPendingDocumentType(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {m.task_requested_documents_existing_confirm_title()}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {pendingDocumentType &&
                      m.task_requested_documents_existing_confirm_description({
                        documentType: m.document_type_label({ documentType: pendingDocumentType }),
                      })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{m.task_cancel()}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (!pendingDocumentType) return;
                      addRequestedDocument(pendingDocumentType);
                      setPendingDocumentType(null);
                    }}
                  >
                    {m.task_requested_documents_existing_confirm_action()}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        );
      }}
    </form.Field>
  );
};

/** The API rejects a blank `other` description; say so here instead of via a 400. */
const describeUnlabelledOther = (
  entries: TaskFormValues["requestedDocuments"],
): string | undefined =>
  entries.some((entry) => entry.documentType === "other" && !entry.label?.trim())
    ? m.task_requested_documents_other_required()
    : undefined;
